import { useMemo, useState, type ReactNode } from 'react'
import { Elements, PaymentElement, CardElement, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js'
import type { StripeElementsOptions } from '@stripe/stripe-js'
import { Sparkles, Loader2, Lock, Code2 } from 'lucide-react'
import { getStripe, resolveStripePublishableKey, lightAppearance } from '@/lib/stripe'
import { Button } from '@/components/ui/button'

const paymentElementFields = {
  billingDetails: {
    address: 'never' as const,
  },
}

const paymentElementTerms = {
  applePay: 'never' as const,
  auBecsDebit: 'never' as const,
  bancontact: 'never' as const,
  card: 'never' as const,
  cashapp: 'never' as const,
  googlePay: 'never' as const,
  ideal: 'never' as const,
  klarna: 'never' as const,
  paypal: 'never' as const,
  sepaDebit: 'never' as const,
  sofort: 'never' as const,
  usBankAccount: 'never' as const,
}

const stripePromise = getStripe()

const TRIAL_AMOUNT_CENTS = 100
const TRIAL_PRICE_LABEL = '$1'
const POST_TRIAL_PRICE = 29

const elementsOptions: StripeElementsOptions = {
  mode: 'subscription',
  amount: TRIAL_AMOUNT_CENTS,
  currency: 'usd',
  paymentMethodCreation: 'manual',
  appearance: lightAppearance,
}

const cardElementStyle = {
  style: {
    base: {
      fontSize: '15px',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontWeight: '500',
      color: '#0f172a',
      '::placeholder': { color: '#94a3b8' },
    },
    invalid: { color: '#dc2626' },
  },
}

function SubmitButton({ label = 'Start $1 Trial' }: { label?: string }) {
  const stripe = useStripe()
  const elements = useElements()
  const [loading, setLoading] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)

  const onClick = async () => {
    if (!stripe || !elements) return
    setLoading(true)
    setMsg(null)
    const { error } = await elements.submit()
    setLoading(false)
    setMsg(error ? error.message ?? 'invalid input' : 'preview only — no charge made')
  }

  return (
    <div className="mt-3 space-y-2">
      <Button
        onClick={onClick}
        disabled={loading || !stripe}
        size="default"
        className="w-full font-semibold text-[15px] h-10 rounded-lg bg-foreground text-background hover:bg-foreground/90"
      >
        {loading ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Processing...
          </>
        ) : (
          label
        )}
      </Button>
      {msg && <p className="text-[11px] text-center text-muted-foreground">{msg}</p>}
    </div>
  )
}

function CardFrame({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-teal-600" />
          <span className="text-[15px] font-semibold text-foreground">Pro Plan</span>
        </div>
        <span className="text-[11px] text-muted-foreground">3-day trial</span>
      </div>
      <p className="text-[14px] text-muted-foreground mb-3 leading-snug">
        Try Pro for <span className="text-foreground font-semibold">{TRIAL_PRICE_LABEL}</span> — 3 days of full access, then ${POST_TRIAL_PRICE}/mo.
      </p>
      {children}
    </div>
  )
}

function FieldBox({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-background px-3.5 py-3">
      {children}
    </div>
  )
}

// 1. CardElement — Stripe's legacy single-line element
function VariantCardElement() {
  return (
    <CardFrame>
      <FieldBox>
        <CardElement options={cardElementStyle} />
      </FieldBox>
      <SubmitButton />
    </CardFrame>
  )
}

// 2. Split fields — own layout
function VariantSplitFields() {
  return (
    <CardFrame>
      <div className="space-y-2">
        <FieldBox>
          <CardNumberElement options={{ ...cardElementStyle, placeholder: 'Card number' }} />
        </FieldBox>
        <div className="grid grid-cols-2 gap-2">
          <FieldBox>
            <CardExpiryElement options={cardElementStyle} />
          </FieldBox>
          <FieldBox>
            <CardCvcElement options={cardElementStyle} />
          </FieldBox>
        </div>
      </div>
      <SubmitButton />
    </CardFrame>
  )
}

// 3. Split fields — grouped, single border with dividers
function VariantSplitFieldsGrouped() {
  return (
    <CardFrame>
      <div className="rounded-lg border border-border bg-background overflow-hidden divide-y divide-border">
        <div className="px-3.5 py-3">
          <CardNumberElement options={{ ...cardElementStyle, placeholder: 'Card number' }} />
        </div>
        <div className="grid grid-cols-2 divide-x divide-border">
          <div className="px-3.5 py-3">
            <CardExpiryElement options={cardElementStyle} />
          </div>
          <div className="px-3.5 py-3">
            <CardCvcElement options={cardElementStyle} />
          </div>
        </div>
      </div>
      <SubmitButton />
    </CardFrame>
  )
}

// 4. PaymentElement default (accordion + radios)
function VariantPaymentElementDefault() {
  return (
    <CardFrame>
      <PaymentElement options={{ layout: { type: 'accordion', defaultCollapsed: false, radios: 'always', spacedAccordionItems: false }, fields: paymentElementFields, terms: paymentElementTerms }} />
      <SubmitButton />
    </CardFrame>
  )
}

// 5. PaymentElement — tabs
function VariantPaymentElementTabs() {
  return (
    <CardFrame>
      <PaymentElement options={{ layout: 'tabs', fields: paymentElementFields, terms: paymentElementTerms }} />
      <SubmitButton />
    </CardFrame>
  )
}

// 6. PaymentElement — accordion spaced, no radios
function VariantPaymentElementSpaced() {
  return (
    <CardFrame>
      <PaymentElement options={{ layout: { type: 'accordion', defaultCollapsed: false, radios: 'never', spacedAccordionItems: true }, fields: paymentElementFields, terms: paymentElementTerms }} />
      <SubmitButton />
    </CardFrame>
  )
}

type ImplKind = 'elements-card' | 'elements-split' | 'elements-payment'

const IMPL_META: Record<ImplKind, { label: string; detail: string }> = {
  'elements-card': {
    label: 'Stripe Elements · CardElement',
    detail: 'Single iframe for the whole card row. We own the wrapper, Stripe owns the field. Legacy — Stripe recommends PaymentElement for new builds.',
  },
  'elements-split': {
    label: 'Stripe Elements · split fields',
    detail: 'Three separate Stripe iframes (CardNumber, CardExpiry, CardCvc). We control layout, padding, focus borders. Maximum visual control.',
  },
  'elements-payment': {
    label: 'Stripe Elements · PaymentElement',
    detail: 'Drop-in Stripe-rendered form. Auto-supports many payment methods, Link 1-click for returning Stripe users. Less visual control over field arrangement.',
  },
}

const VARIANTS: { id: string; el: () => JSX.Element; impl: ImplKind; note: string }[] = [
  { id: '1-card-element', el: VariantCardElement, impl: 'elements-card', note: 'Most compact. Matches the screenshot you shared. Single horizontal row.' },
  { id: '2-split-fields', el: VariantSplitFields, impl: 'elements-split', note: 'Card number on top, expiry + CVC in a row below. Each field in its own box.' },
  { id: '3-split-fields-grouped', el: VariantSplitFieldsGrouped, impl: 'elements-split', note: 'Same fields as #2 but unified into one bordered group with dividers.' },
  { id: '4-payment-default', el: VariantPaymentElementDefault, impl: 'elements-payment', note: 'Default accordion layout with radio buttons for payment-method selection.' },
  { id: '5-payment-tabs', el: VariantPaymentElementTabs, impl: 'elements-payment', note: 'Payment methods sit across the top as tabs.' },
  { id: '6-payment-spaced', el: VariantPaymentElementSpaced, impl: 'elements-payment', note: 'Spaced accordion, no radios. Cleaner when card is the dominant method.' },
]

export default function AdminPaymentExamplesPage() {
  const { isDemo } = useMemo(() => resolveStripePublishableKey(), [])

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">Inline Checkout Options</h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
          Visual comparison of Stripe Elements layouts for the in-chat upgrade card. Same plan copy and frame across all variants — the difference is the payment-collection treatment.
        </p>
      </div>

      {isDemo && (
        <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-[13px] text-amber-900 flex items-start gap-2">
          <Lock className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <strong>Visual preview only.</strong> Using Stripe&apos;s public demo key because <code className="text-[12px] bg-amber-100 px-1 rounded">VITE_STRIPE_PUBLISHABLE_KEY</code> isn&apos;t set in <code className="text-[12px] bg-amber-100 px-1 rounded">.env.local</code>.
          </div>
        </div>
      )}

      <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-3 text-[12px]">
        <div className="rounded-lg border border-border bg-secondary px-3 py-2.5">
          <div className="font-semibold text-foreground mb-0.5">Stripe Hosted Checkout</div>
          <div className="text-muted-foreground leading-snug">Redirect to Stripe-hosted page. <span className="text-foreground/80">Current implementation.</span></div>
        </div>
        <div className="rounded-lg border border-border bg-secondary px-3 py-2.5">
          <div className="font-semibold text-foreground mb-0.5">Stripe Elements</div>
          <div className="text-muted-foreground leading-snug">Stripe-rendered fields embedded in our own UI. <span className="text-foreground/80">All variants below use this.</span></div>
        </div>
        <div className="rounded-lg border border-border bg-secondary px-3 py-2.5 opacity-50">
          <div className="font-semibold text-foreground mb-0.5">Stripe Embedded Checkout</div>
          <div className="text-muted-foreground leading-snug">Full Stripe checkout in an iframe. <span className="text-foreground/80">Ruled out — too rigid.</span></div>
        </div>
      </div>

      <Elements stripe={stripePromise} options={elementsOptions}>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {VARIANTS.map(({ id, el: El, impl, note }) => {
            const meta = IMPL_META[impl]
            return (
              <div key={id} className="flex flex-col gap-3">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-[12px] font-mono text-muted-foreground">{id}</span>
                </div>
                <Elements stripe={stripePromise} options={elementsOptions}>
                  <El />
                </Elements>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5 text-[11px] font-mono text-foreground bg-secondary border border-border rounded px-2 py-1 w-fit">
                    <Code2 className="h-3 w-3 text-muted-foreground" />
                    {meta.label}
                  </div>
                  <p className="text-[12px] text-muted-foreground leading-snug">{note}</p>
                  <p className="text-[11px] text-muted-foreground/80 leading-snug italic">{meta.detail}</p>
                </div>
              </div>
            )
          })}
        </div>
      </Elements>

    </div>
  )
}
